import { CommonModule } from '@angular/common';
import {
    Component,
    InputSignal,
    OnDestroy,
    Signal,
    signal,
    WritableSignal,
    computed,
    inject,
    input,
    output
} from '@angular/core';
import { IqEventBusBrokerHandler, IqEventBusBrokerService } from '@dat/iq-event-bus';
import { get, kebabCase, set } from 'lodash';
import { toLower } from 'ramda';
import {
    EquipmentTypes,
    Fuel,
    IRowClick,
    ODGeoSearchPair,
    UiGraphCard,
    UiLane,
    UiSpotRate,
    emptyUiGraphCard,
    emptyUiLane,
    emptyUiSpotRate,
    flatbedLanes,
    reeferLanes,
    vanLanes,
} from '../models';
import { IqTableLanesStateModule } from '../state/iq-table-lanes-state.module';
import { IqRateLaneTableLanesStateService } from '../state/services/iq-rate-lane-table-lanes-state.service';
import { DatiqTableComponent } from '@dat/datiq-components-pkg/table';

@Component({
    selector: 'iq-rate-lane-table-lanes',
    standalone: true,
    imports: [CommonModule, IqTableLanesStateModule, DatiqTableComponent],
    templateUrl: './table-lanes.component.html',
    styleUrl: './table-lanes.component.scss',
})
export class TableLanesComponent implements OnDestroy {
    id: InputSignal<string> = input.required();

    equipmentType: WritableSignal<EquipmentTypes> = signal(null);

    fuel: InputSignal<Fuel> = input(false);

    rowClick = output<IRowClick>();

    spotRateLabelSignals: Signal<Signal<UiSpotRate>[]> = computed(() => {
        return this.lanes()?.map((ODPairs: ODGeoSearchPair) => {
            return this._state.getSpotRate(ODPairs, this.equipmentType());
        }) ?? [];
    });

    odPairlaneSignals: Signal<Signal<UiLane>[]> = computed(() => {
        return this.lanes()?.map((ODPairs: ODGeoSearchPair) => {
            return this._state.getLane(ODPairs);
        }) ?? [];
    });

    forecast52WeekSignal: Signal<Signal<UiGraphCard>[]> = computed(() => {
        return this.lanes()?.map((ODPairs: ODGeoSearchPair) => {
            return this._state.getForecast52WeeksGraph(
                ODPairs,
                this.equipmentType(),
            );
        }) ?? [];
    });

    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    private _dispatchActions: Record<string, IqEventBusBrokerHandler>;

    constructor(private readonly _state: IqRateLaneTableLanesStateService) {

        this._dispatchActions = this._eventBus.registerMap({
            'setLandingEquipmentType': {
                callback: (payload: any) => {
                    const newEquipmentType = payload?.equipmentType;
                    if(newEquipmentType) {
                        this.equipmentType.set(newEquipmentType);
                    }
                },
            },
        });
    }

    lanes: Signal<ODGeoSearchPair[]> = computed(() => {
        const lanesMap: Record<EquipmentTypes, ODGeoSearchPair[]> = {
            VAN: vanLanes,
            FLATBED: flatbedLanes,
            REEFER: reeferLanes,
        };


        return lanesMap[this.equipmentType()];
    });

    table: Signal<any> = computed(() => {
        return {
            id: this.id(),
            rows: this.rows(),
            headerRow: this.headerRow(),
            showAll: true,
            pageSize: 5,
            interactive: true,
        };
    });

    rows: Signal<any[]> = computed(() => {
        const spotRateLabels: UiSpotRate[] = this.spotRateLabels();

        const lane: UiLane[] = this.odPairlanes();

        const forecast52WeeksGraph = this.forecast52WeeksGraph();

        const columns: {
            spotRate: UiSpotRate[];
            lane: UiLane[];
            forecast52WeeksGraph: UiGraphCard[];
        } = {
            spotRate: spotRateLabels,
            lane,
            forecast52WeeksGraph,
        };

        const columnKeys = Object.keys(columns);

        return [...columnKeys].reduce((currentRows, columnKey) => {
            return columns[columnKey].reduce((newRows, columnValue, i) => {
                const path = `${i}.columns.${columnKey}.cell`;

                return set(newRows, path, {
                    ...get(newRows, path),
                    ...columnValue,
                });
            }, currentRows);
        }, this._defaultRows());
    });

    private _defaultRows: Signal<any[]> = computed(() => {
        return (
            this.lanes()?.map((ODPairs: ODGeoSearchPair, i: number) => {
                return {
                    id: this.createId(['row', `${i}`]),
                    colOrder: ['lane', 'spotRate', 'forecast52WeeksGraph'],
                    index: i,
                    columns: {
                        spotRate: {
                            id: this.createId([
                                'row',
                                `${i}`,
                                kebabCase('spotRate'),
                            ]),
                            type: 'label',
                            cell: {
                                ...emptyUiSpotRate,
                                id: this.createId([
                                    'row',
                                    `${i}`,
                                    kebabCase('spotRate'),
                                ]),
                            },
                        },
                        forecast52WeeksGraph: {
                            id: this.createId([
                                'row',
                                `${i}`,
                                kebabCase('forecast52WeeksGraph'),
                            ]),
                            type: 'graph-value',
                            cell: this.defaultForecast52WeekGraph(),
                        },
                        lane: {
                            id: this.createId([
                                'row',
                                `${i}`,
                                kebabCase('lane'),
                            ]),
                            type: 'lane',
                            cell: {
                                ...emptyUiLane,
                                id: this.createId([
                                    'row',
                                    `${i}`,
                                    kebabCase('lane'),
                                ]),
                            },
                        },
                    },
                };
            }) ?? []
        );
    });

    headerRow: Signal<any> = computed(() => {
        return {
            id: this.id() + '-row-header',
            colOrder: ['lane', 'spotRate', 'forecast52WeeksGraph'],
            columns: {
                lane: {
                    id: this.createId([kebabCase('lane'), 'row-header']),
                    type: 'header-sort',
                    cell: {
                        id: this.createId(['row-header', kebabCase('lane')]),
                        label: 'Origin/Destination',
                        sortBy: '',
                        disabled: true,
                    },
                },
                spotRate: {
                    id: this.createId([kebabCase('spotRate'), 'row-header']),
                    type: 'header-sort',
                    cell: {
                        id: this.createId([
                            'row-header',
                            kebabCase('spotRate'),
                        ]),
                        label: this.fuel() ? 'Spot+Fuel' : 'Spot Rate',
                        sortBy: '',
                        disabled: true,
                    },
                },
                forecast52WeeksGraph: {
                    id: this.createId([
                        kebabCase('forecast52WeeksGraph'),
                        'row-header',
                    ]),
                    type: 'header-sort',
                    cell: {
                        id: this.createId([
                            'row-header',
                            kebabCase('forecast52WeeksGraph'),
                        ]),
                        label: '52 Week Forecast',
                        sortBy: '',
                        disabled: true,
                    },
                },
            },
            index: 0,
        };
    });

    spotRateLabels: Signal<UiSpotRate[]> = computed(() => {
        return (
            this.spotRateLabelSignals()?.reduce((labels, signal, i) => {
                const label = signal();
                const newLabels = set(labels, `${i}`, {
                    ...get(labels, `${i}`),
                    ...label,
                    id: this.createId(['row', `${i}`, kebabCase('spotRate')]),
                });
                return [...newLabels];
            }, this._defaultSpotRateLabels()) ?? this._defaultSpotRateLabels()
        );
    });

    private _defaultSpotRateLabels(): UiSpotRate[] {
        return [...(this.lanes() ?? [])].map((): UiSpotRate => emptyUiSpotRate);
    }

    odPairlanes: Signal<UiLane[]> = computed((): UiLane[] => {
        return (
            this.odPairlaneSignals()?.reduce((lanes, signal, i) => {
                const odPair = signal();
                const newLanes = set(lanes, `${i}`, {
                    ...get(lanes, `${i}`),
                    ...odPair,
                    id: this.createId(['row', `${i}`, kebabCase('lane')]),
                });

                return [...newLanes];
            }, this._defaultLanes()) ?? this._defaultLanes()
        );
    });

    forecast52WeeksGraph: Signal<UiGraphCard[]> = computed(
        (): UiGraphCard[] => {
            return (
                this.forecast52WeekSignal()?.reduce((graph, signal, i) => {
                    const forecast = signal();
                    const newGraph = set(graph, `${i}`, {
                        ...get(graph, `${i}`),
                        ...forecast,
                        id: this.createId([
                            'row',
                            `${i}`,
                            kebabCase('forecast52WeeksGraph'),
                        ]),
                    });
                    return [...newGraph];
                }, this.defaultForecast52WeekGraph()) ??
                this.defaultForecast52WeekGraph()
            );
        },
    );

    private _defaultLanes(): UiLane[] {
        return [...(this.lanes() ?? [])].map((): UiLane => emptyUiLane);
    }

    private defaultForecast52WeekGraph(): UiGraphCard[] {
        return [...(this.lanes() ?? [])].map(
            (): UiGraphCard => emptyUiGraphCard,
        );
    }

    private createId(parts: string[]): string {
        return [this.id(), ...parts].join('-');
    }

    navigateToRateviewLane(row: any): void {
        const cell: any = row.columns.lane.cell as any;

        /**
         * This dispatches an event when the component row is clicked
         *
         *
         * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
         *
         * ```json
         * {
         *  "onClick": [
         *    {
         *      "name": "EVENT_NAME",
         *      "payload": {},
         *      "sourceEventRule": {
         *          "payload": [
         *            {
         *                "path": "id",
         *                "operator": "eq",
         *                "value": "COMPONENT_ID"
         *            },
         *            {
         *                "path": "componentName",
         *                "operator": "eq",
         *                "value": "TableLanes"
         *            },
         *            {
         *                "path": "componentOrigin",
         *                "operator": "eq",
         *                "value": "Row"
         *            },
         *          ]
         *      },
         *      "rules": []
         *    },
         *   ],
         * }
         * ```
         */
        const dispatch = () =>
            this._eventBus.dispatch({
                name: 'navigateToUrl',
                payload: {
                    id: this.id(),
                    componentName: 'TableLanes',
                    componentOrigin: 'Row',
                    value: {
                        origin: `${toLower(cell.origin.city)}-${toLower(
                            cell.origin.stateProv,
                        )}`,
                        destination: `${toLower(cell.destination.city)}-${toLower(
                            cell.destination.stateProv,
                        )}`,
                        equipment: this.equipmentType(),
                    },
                    url: `/rateview/${toLower(cell.origin.city)}-${toLower(cell.origin.stateProv)}~${toLower(cell.destination.city)}-${toLower(cell.destination.stateProv)}~${this.equipmentType()}`,
                },
            });

        dispatch();
    }

    ngOnDestroy() {
        this._eventBus.unregisterMap(this._dispatchActions);
    }
}
